import { signOut as cognitoSignOut } from "aws-amplify/auth";
import { jwtDecode } from "jwt-decode";

enum CognitoStorageMagics {
  LastAuthUser = "LastAuthUser",
  IdToken = "idToken",
  RefreshToken = "refreshToken",
  AccessToken = "accessToken",
}

export async function synchPhpSessionWithCognitoStorage(
  userPoolsWebClientId: string,
): Promise<void> {
  if (!window._msAuth?.session) {
    return;
  }
  const { session } = window._msAuth;
  await setCognitoStorage(
    userPoolsWebClientId,
    session.accessToken,
    session.refreshToken,
    session.idToken,
    session.email,
  );
  // remove the <script> element that contains window._msAuth=
  document.querySelector("script[id=msAuth]")?.remove();
  delete window._msAuth.session;
}

export async function signOut(): Promise<void> {
  delete window._msAuth?.session;
  await cognitoSignOut();
}

// Wipe any leftover Cognito tokens from a previous (possibly different) user.
// Call this before starting an alt-auth flow (PAT, Google) so Amplify doesn't
// silently reuse a stale identity from localStorage.
export function clearCognitoLocalStorage(): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("CognitoIdentityServiceProvider")) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    localStorage.removeItem(key);
  }
}

export async function setCognitoStorage(
  userPoolsWebClientId: string,
  accessToken: string,
  refreshToken: string,
  idToken: string,
  email?: string,
): Promise<void> {
  const cognitoStorage: Array<[string, string | null]> = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("CognitoIdentityServiceProvider")) {
      cognitoStorage.push([key, localStorage.getItem(key)]);
    }
  }
  if (!accessToken || !refreshToken || !idToken) {
    return;
  }
  const setCognitoSessionItem = (itemKey: Array<string>, value: string) => {
    const key = [
      "CognitoIdentityServiceProvider",
      userPoolsWebClientId,
      ...itemKey,
    ].join(".");
    localStorage.setItem(key, value);
  };

  const oldEmail = cognitoStorage.find(([key]) =>
    key?.endsWith(CognitoStorageMagics.LastAuthUser),
  );
  const payload = jwtDecode<{ "cognito:username": string }>(idToken);

  if (
    oldEmail &&
    oldEmail[1] !== payload["cognito:username"] &&
    email &&
    oldEmail[1] !== email
  ) {
    await signOut();
  } else {
    cognitoStorage.forEach(([key]) => localStorage.removeItem(key));
  }

  setCognitoSessionItem(
    [CognitoStorageMagics.LastAuthUser],
    payload["cognito:username"],
  );
  setCognitoSessionItem(
    [payload["cognito:username"], CognitoStorageMagics.IdToken],
    idToken,
  );
  setCognitoSessionItem(
    [payload["cognito:username"], CognitoStorageMagics.RefreshToken],
    refreshToken,
  );
  setCognitoSessionItem(
    [payload["cognito:username"], CognitoStorageMagics.AccessToken],
    accessToken.replace(/^jwt:/, ""),
  );
}
